import React from "react";
import PropTypes from "prop-types";

const SIGNATURE_PAD_SCRIPT_ID = "signature-pad-lib";
const DEFAULT_SCRIPT_URL = "https://cdn.jsdelivr.net/npm/signature_pad@5.1.3/dist/signature_pad.umd.min.js";

let signaturePadLoader = null;

const loadSignaturePad = scriptUrl => {
    if (window.SignaturePad) {
        return Promise.resolve(window.SignaturePad);
    }

    if (signaturePadLoader) {
        return signaturePadLoader;
    }

    signaturePadLoader = new Promise((resolve, reject) => {
        const existing = document.getElementById(SIGNATURE_PAD_SCRIPT_ID);
        if (existing) {
            if (window.SignaturePad) {
                resolve(window.SignaturePad);
                return;
            }
            existing.addEventListener("load", () => {
                if (window.SignaturePad) {
                    resolve(window.SignaturePad);
                } else {
                    reject(new Error("SignaturePad non disponibile"));
                }
            });
            existing.addEventListener("error", () => reject(new Error("Errore caricamento firma")));
            return;
        }

        const script = document.createElement("script");
        script.id = SIGNATURE_PAD_SCRIPT_ID;
        script.async = true;
        script.src = scriptUrl;
        script.onload = () => {
            if (window.SignaturePad) {
                resolve(window.SignaturePad);
            } else {
                reject(new Error("SignaturePad non disponibile"));
            }
        };
        script.onerror = () => reject(new Error("Errore caricamento firma"));
        document.body.appendChild(script);
    });

    return signaturePadLoader;
};

export default class SignaturePadCanvas extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            error: null
        };
        this.canvasRef = React.createRef();
        this.signaturePad = null;
        this.canvasStyle = {
            width: "100%",
            height: this.props.height || "220px",
            border: "1px solid #e1e1e1",
            borderRadius: "6px",
            backgroundColor: "#ffffff"
        };
        this._mounted = false;
    }

    componentDidMount() {
        this._mounted = true;
        window.addEventListener("resize", this.handleResize);
        loadSignaturePad(this.props.scriptUrl || DEFAULT_SCRIPT_URL)
            .then(SignaturePadConstructor => {
                if (!this._mounted) return;
                this.initSignaturePad(SignaturePadConstructor);
                this.setState({loading: false, error: null}, _ => {
                    if (this.props.onReady) {
                        this.props.onReady();
                    }
                });
            })
            .catch(error => {
                if (!this._mounted) return;
                this.setState({loading: false, error: error}, _ => {
                    if (this.props.onError) {
                        this.props.onError(error);
                    }
                });
            });
    }

    componentWillUnmount() {
        this._mounted = false;
        window.removeEventListener("resize", this.handleResize);
    }

    initSignaturePad = SignaturePadConstructor => {
        if (!this.canvasRef.current || !SignaturePadConstructor) return;
        this.signaturePad = new SignaturePadConstructor(this.canvasRef.current, {
            penColor: this.props.penColor || "#293069",
            backgroundColor: this.props.backgroundColor || "rgba(255,255,255,0)"
        });
        this.resizeCanvas();
    }

    resizeCanvas = _ => {
        const canvas = this.canvasRef.current;
        if (!canvas) return;
        const ratio = Math.max(window.devicePixelRatio || 1, 1);
        canvas.width = canvas.offsetWidth * ratio;
        canvas.height = canvas.offsetHeight * ratio;
        const ctx = canvas.getContext("2d");
        if (ctx) {
            ctx.scale(ratio, ratio);
        }
        if (this.signaturePad) {
            this.signaturePad.clear();
        }
    }

    handleResize = _ => {
        this.resizeCanvas();
    }

    clear = _ => {
        if (this.signaturePad) {
            this.signaturePad.clear();
        }
    }

    isEmpty = _ => {
        if (!this.signaturePad) return true;
        return this.signaturePad.isEmpty();
    }

    toSVG = _ => {
        if (!this.signaturePad) return null;
        return this.signaturePad.toSVG();
    }

    isReady = _ => !!this.signaturePad;

    render() {
        return (
            <div>
                {
                    this.state.loading &&
                    <div className={"text-muted small mb-2"}>Caricamento firma...</div>
                }
                {
                    this.state.error &&
                    <div className={"text-danger small mb-2"}>Firma non disponibile.</div>
                }
                <canvas ref={this.canvasRef} style={this.canvasStyle}/>
            </div>
        );
    }
}

SignaturePadCanvas.propTypes = {
    scriptUrl: PropTypes.string,
    height: PropTypes.string,
    penColor: PropTypes.string,
    backgroundColor: PropTypes.string,
    onReady: PropTypes.func,
    onError: PropTypes.func
};
